import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/invoices/models/inputs/get-invoices-paginated.model';
import { InvoiceModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/invoice.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { CustomerFilterStateController } from '@app/feature/customer/customer-filter-state/customer-filter-state.controller';
import { NetworkFilterStateController } from '@app/feature/network/network-filter-state/network-filter-state.controller';

import { InvoiceStatusFilterStateController } from '../invoice-status-filter-state/invoice-status-filter-state.controller';
import { InvoiceTypeFilterStateController } from '../invoice-type-filter-state/invoice-type-filter-state.controller';
import { InvoiceListCreatedAtColumn } from './invoice-list-created-at.column';
import { InvoiceListCustomerColumn } from './invoice-list-customer.column';
import { InvoiceListInvoiceNumberColumn } from './invoice-list-invoice-number.column';
import { InvoiceListIssuedAtColumn } from './invoice-list-issued-at.column';
import { InvoiceListNetworkColumn } from './invoice-list-network.column';
import { InvoiceListPeriodEndColumn } from './invoice-list-period-end.column';
import { InvoiceListPeriodStartColumn } from './invoice-list-period-start.column';
import { InvoiceListStatusColumn } from './invoice-list-status.column';
import { InvoiceListTotalTTCColumn } from './invoice-list-total-ttc.column';
import { InvoiceListTypeColumn } from './invoice-list-type.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const INVOICE_LIST_SORT_FIELD: NonNullable<
  GetInvoicesPaginatedModel['sortField']
> = 'createdAt';

export const INVOICE_LIST_SORT_ORDER: NonNullable<
  GetInvoicesPaginatedModel['sortOrder']
> = -1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetInvoicesPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const INVOICE_LIST_FILTER: ResourceListFilter = {
  customerId: {
    componentType: CustomerFilterStateController,
  },
  networkId: {
    componentType: NetworkFilterStateController,
  },
  status: {
    componentType: InvoiceStatusFilterStateController,
  },
  type: {
    componentType: InvoiceTypeFilterStateController,
  },
} as const;

export const INVOICE_LIST_FILTER_NAME_LIST = Object.keys(
  INVOICE_LIST_FILTER,
) as Array<keyof typeof INVOICE_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const invoiceListColumnHelper = createColumnHelper<InvoiceModel>();

type ResourceColumnType = ColumnDef<InvoiceModel, any>[];

export const INVOICE_LIST_COLUMN_LIST: ResourceColumnType = [
  invoiceListColumnHelper.accessor('createdAt', InvoiceListCreatedAtColumn),
  invoiceListColumnHelper.accessor(
    'invoiceNumber',
    InvoiceListInvoiceNumberColumn,
  ),
  invoiceListColumnHelper.accessor('issuedAt', InvoiceListIssuedAtColumn),
  invoiceListColumnHelper.accessor('periodStart', InvoiceListPeriodStartColumn),
  invoiceListColumnHelper.accessor('periodEnd', InvoiceListPeriodEndColumn),
  invoiceListColumnHelper.accessor('customer', InvoiceListCustomerColumn),
  invoiceListColumnHelper.accessor('network', InvoiceListNetworkColumn),
  invoiceListColumnHelper.accessor('totalTTC', InvoiceListTotalTTCColumn),
  invoiceListColumnHelper.accessor('status', InvoiceListStatusColumn),
  invoiceListColumnHelper.accessor('type', InvoiceListTypeColumn),
];

export const INVOICE_LIST_COLUMN_ID_LIST = INVOICE_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const INVOICE_LIST_COLUMN_ORDER = [...INVOICE_LIST_COLUMN_ID_LIST];

export const INVOICE_LIST_COLUMN_VISIBILITY =
  INVOICE_LIST_COLUMN_ID_LIST.reduce((columnVisibility, columnId) => {
    return { ...columnVisibility, [columnId]: true };
  }, {});

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface InvoiceListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const INVOICE_LIST_DEFAULT_OPTIONS: InvoiceListOptionsModel = {
  columnVisibility: INVOICE_LIST_COLUMN_VISIBILITY,
  columnOrder: INVOICE_LIST_COLUMN_ORDER,
};

export const INVOICE_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  invoiceNumber: 'delmonicos-shared-sdk.outputs-invoice.invoice-number',
  issuedAt: 'delmonicos-shared-sdk.outputs-invoice.issued-at',
  periodStart: 'delmonicos-shared-sdk.outputs-invoice.period-start',
  periodEnd: 'delmonicos-shared-sdk.outputs-invoice.period-end',
  customer: 'delmonicos-shared-sdk.outputs-invoice.customer',
  network: 'delmonicos-shared-sdk.outputs-invoice.network',
  totalTTC: 'delmonicos-shared-sdk.outputs-invoice.total-ttc',
  status: 'delmonicos-shared-sdk.outputs-invoice.status',
  type: 'delmonicos-shared-sdk.outputs-invoice.type',
  createdAt: 'delmonicos-shared-sdk.outputs-base-with-timestamp.created-at',
};
